import { useQuery } from "@tanstack/react-query";
import { apiClient as authedApi } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Gem } from "lucide-react";
import { RAIL_ROW } from "./layout/navItems";

interface SubscriptionOverview {
  planName?: string;
}

const fetchOverview = async (): Promise<SubscriptionOverview> => {
  const resp = await authedApi.get(`/subscription-overview`);
  return resp.data;
};

/**
 * Free-plan upsell, pinned directly under the brand mark so the upgrade path
 * sits in the rail's most visible band rather than below the fold of a long
 * nav list.
 *
 * It borrows the nav rows' geometry on purpose. Sitting above the nav means
 * anything that changes height between the two rail states would shunt every
 * link below it up and down mid-transition, so the collapsed and expanded
 * forms are the same row: same padding, same 5-unit icon as the label's line
 * box, and the label going `sr-only` rather than out of the markup. Only the
 * text appears and disappears; the row's height never moves.
 */
export default function UpgradeBanner({
  collapsed = false,
  onPointerEnter,
}: {
  collapsed?: boolean;
  onPointerEnter?: () => void;
}) {
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["subscriptionOverview"],
    queryFn: fetchOverview,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const isFreePlan = (data?.planName ?? "").toLowerCase() === "free";

  if (!isFreePlan) return null;

  return (
    <div className="shrink-0 px-2 pt-3" onPointerEnter={onPointerEnter}>
      <button
        type="button"
        onClick={() => navigate("/settings/billing/change-plan")}
        title="Upgrade your plan"
        aria-label="Upgrade your plan"
        className={`${RAIL_ROW} border border-rose-600/40 bg-rose-500/5 hover:border-rose-500/70 hover:bg-rose-500/10`}
      >
        <Gem className="h-5 w-5 shrink-0 text-rose-500" />
        <span
          className={
            collapsed
              ? "sr-only"
              : "truncate bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text font-semibold text-transparent"
          }
        >
          Upgrade plan
        </span>
      </button>
    </div>
  );
}
